/**
 * PDP logo-version A/B/C test.
 *
 * While the smaller logo rolls out, a product can exist as two PDPs — same title, same
 * colour, different handle, both tagged window.oaLogoVersionsTag. When a size sells out on
 * the one being viewed it may still be in stock on the other. This offers that other one,
 * three ways, and the A/B tool picks which:
 *
 *   a  Logo selector tabs above the colour rail, plus a hint under the size picker.
 *   b  Alternative module under the CTA, revealed when the chosen size is sold out here.
 *   c  Missing-stock popup — the existing quick-add sidebar, opened for the sibling.
 *
 * There is no control version that renders something: with no version assigned this file
 * returns before it fetches anything and binds nothing at all.
 *
 * Depends on OALogoVersionsApi, jQuery and the quick-add sidebar (version c only).
 */
(function() {
  'use strict';

  var VERSIONS = { a: true, b: true, c: true };

  // How long after load the version-c popup offers itself when a size is already sold out.
  // The newsletter / sale popup runs on a flat 15s timer (layout/theme.liquid), so this
  // clears it — see maybeAutoOpen for what happens if something is on screen anyway.
  var AUTO_OPEN_DELAY_MS = 5000;

  // Off for now: the popup offers itself on every page load, not once per product per
  // session. Flip to true to bring the cap back — markSeen / alreadySeen below are intact
  // and gate on this alone, so nothing else has to change.
  var AUTO_OPEN_ONCE_PER_SESSION = false;

  // Filled disc with a black "i", so the glyph does not inherit the label's font. currentColor
  // on the disc lets the CSS own the grey (#D9D9D9) rather than hard-coding it twice; the
  // glyph is a fixed black so it stays legible whatever the disc is set to.
  var INFO_ICON_SVG = '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">' +
    '<circle cx="8" cy="8" r="8" fill="currentColor"></circle>' +
    '<path fill="#000" d="M7.1 6.6h1.8v5.1H7.1zM8 3.4a1.05 1.05 0 1 1 0 2.1 1.05 1.05 0 0 1 0-2.1z"></path>' +
    '</svg>';

  var SIZE_TILE = '.oa-product-sizes--item-size';
  var SEEN_KEY_PREFIX = 'oa_logo_versions_seen:';
  var SIZE_PARAM = 'oa_logo_size';

  var state = {
    version: null,
    handle: null,
    pair: null,
    // The sold-out size the shopper last asked about, so version c can highlight it again
    // after the sidebar closes.
    lastMissingSize: null,
    // The colour the version-b module is showing, once the shopper picks one that is not
    // the direct same-colour alternative.
    moduleColour: null,
    autoOpenTimer: null,
    booted: false
  };

  var i18n = {};

  // Suffix for tooltip ids — several info icons can be on the page at once (the b module
  // and the block injected into the notify drawer), and aria-describedby needs each one
  // to point at its own.
  var uid = 0;

  // --- Helpers ---

  function config() {
    return window.oaLogoVersionsPdp || null;
  }

  function activeVersion() {
    if (!window.abtest) return null;
    // What the A/B tool last applied wins over re-deriving it. getLogoVersionsVersion()
    // re-reads url and storage, and while logo_versions_ab_test_active is false that read
    // deliberately ignores the stored assignment — which would throw away a call the tool
    // had just made, against the documented "a call made while the test is off still
    // applies". header.js always calls the setter at parse time, so this is always set.
    if (Object.prototype.hasOwnProperty.call(window.abtest, 'logoVersionsVersion')) {
      var applied = window.abtest.logoVersionsVersion;
      return VERSIONS[applied] ? applied : null;
    }
    if (typeof window.abtest.getLogoVersionsVersion !== 'function') return null;
    var v = window.abtest.getLogoVersionsVersion();
    return VERSIONS[v] ? v : null;
  }

  function handleFromUrl() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  // Stands the feature down rather than spending two round trips on a product it must not
  // offer an alternative for. OALogoVersionsApi applies the same list to its results.
  function isExcludedProduct(tags) {
    var excluded = window.oaLogoVersionsExcludeTags;
    if (!Array.isArray(excluded)) excluded = excluded ? String(excluded).split(',') : [];
    if (typeof window.oaComingSoonHideTag === 'string' && window.oaComingSoonHideTag) {
      excluded = excluded.concat(window.oaComingSoonHideTag.split(','));
    }
    excluded = excluded.map(function(t) { return String(t).trim(); }).filter(Boolean);
    if (!excluded.length || !Array.isArray(tags)) return false;
    return tags.some(function(tag) {
      return excluded.some(function(ex) {
        return String(tag).toLowerCase() === ex.toLowerCase();
      });
    });
  }

  // A handle in the hand-maintained map qualifies on its own. The map exists precisely for
  // pairs that are NOT tagged, so gating entry on the tag meant it was never reached — the
  // feature returned before it ever asked the map anything.
  function isMapped(handle) {
    return !!(handle && window.OALogoVersionsApi &&
      typeof window.OALogoVersionsApi.mappedPair === 'function' &&
      window.OALogoVersionsApi.mappedPair(handle));
  }

  // Coming-soon products are served with their size picker and add-to-cart suppressed, so
  // there is no size selection to react to and nothing to offer an alternative for.
  // oaComingSoonHideTag is only defined when the setting is non-blank.
  function isComingSoon() {
    var tag = window.oaComingSoonHideTag;
    if (typeof tag !== 'string' || !tag) return false;
    var cfg = config();
    if (!cfg || !Array.isArray(cfg.tags)) return false;
    return cfg.tags.some(function(t) { return String(t).toLowerCase() === tag.toLowerCase(); });
  }

  // Normalise a size label so it can be matched across the two products. Either side may
  // carry a length or dual-logo suffix ("M - Regular", "M - Knitted logo") that the other
  // does not, and the tiles store the bare size in data-size-name while the sibling's
  // variants carry the full title.
  function sizeKey(name) {
    if (!name) return '';
    return String(name)
      .replace(/\s*-\s*(short|regular|long)\b/gi, '')
      .replace(/\s*-\s*(knitted|printed)\s+logo\b/gi, '')
      .trim()
      .toUpperCase();
  }

  // The length half of a variant title, for products that come in Short / Regular / Long.
  // sizeKey deliberately strips it, so on its own it would call "XS - Short" and "XS - Long"
  // the same variant — and offer a length the shopper never asked for.
  function lengthKey(name) {
    var match = String(name || '').match(/\b(short|regular|long)\b/i);
    return match ? match[1].toLowerCase() : '';
  }

  // Same size AND same length. Where only one side carries a length — a length product
  // paired with one that has none — the size alone decides, rather than matching nothing.
  function sameSize(a, b) {
    if (sizeKey(a) !== sizeKey(b) || !sizeKey(a)) return false;
    var la = lengthKey(a);
    var lb = lengthKey(b);
    if (!la || !lb) return true;
    return la === lb;
  }

  function variantForSize(product, sizeName) {
    if (!product || !sizeName) return null;
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      if (sameSize(variants[i].option1 || variants[i].title, sizeName)) return variants[i];
    }
    return null;
  }

  // Deliberately pinned to the DIRECT same-colour sibling, not to whatever colour the
  // shopper has since picked: this answers "is there an alternative worth offering at all",
  // which must not change because they browsed to another shade.
  function altVariantForSize(sizeName) {
    if (!state.pair) return null;
    return variantForSize(state.pair.alternative, sizeName);
  }

  function firstAvailableSizeOf(product) {
    var variants = (product && product.variants) || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i].option1 || variants[i].title;
    }
    return null;
  }

  function availableOnAlternative(sizeName) {
    var variant = altVariantForSize(sizeName);
    return !!(variant && variant.available);
  }

  function firstAvailableAlternativeSize() {
    if (!state.pair) return null;
    return firstAvailableSizeOf(state.pair.alternative);
  }

  // A product with nothing left to buy renders no size picker at all —
  // product-fashion-size-input.liquid is wrapped in {% if product.available %} — so there
  // are no tiles to click and no selection to react to. That is the case where the other
  // branding version matters most, so it gets its own path rather than falling through
  // the size-driven checks.
  function fullySoldOutHere() {
    var tiles = visibleTiles();
    // The tiles win when there are any. They are server-rendered from the request the
    // shopper is looking at, whereas current.available is the Storefront API's
    // availableForSale, cached for five minutes — so it can still say the product is gone
    // while every size on screen is buyable, and that made all three versions claim "size
    // sold out?" on a fully stocked picker.
    if (tiles.length) {
      return tiles.every(function(tile) { return tile.classList.contains('disabled'); });
    }
    // No picker at all: product-fashion-size-input renders none when nothing is buyable,
    // so here the API flag is the only signal there is.
    return !!(state.pair && state.pair.current && state.pair.current.available === false);
  }

  // The size tiles the shopper can actually see. The length and dual-logo pickers hide the
  // tiles belonging to the other length, and a hidden tile is not a sold-out size the
  // shopper is looking at.
  function visibleTiles() {
    var tiles = document.querySelectorAll('.fashion-info-sticky ' + SIZE_TILE);
    return Array.prototype.filter.call(tiles, function(tile) {
      if (tile.closest('#cts-modal') || tile.closest('#product-stl')) return false;
      return !!(tile.offsetWidth || tile.offsetHeight || tile.getClientRects().length);
    });
  }

  // Read the selection off the tiles, never off #variant-id: that input keeps the last
  // in-stock variant after a sold-out click, because product-single.layout.js gates its
  // whole handler on the tile not being disabled.
  function selectedTile() {
    var tiles = visibleTiles();
    for (var i = 0; i < tiles.length; i++) {
      if (tiles[i].classList.contains('active')) return tiles[i];
    }
    return null;
  }

  // data-size-name is the bare size ("XS") — product-fashion-size-input strips the length out
  // of it, because the length is a separate picker. data-title keeps the whole variant title
  // ("XS - Short"), which is what has to travel so the right length is matched on the
  // sibling and carried across a switch.
  function tileSizeName(tile) {
    if (!tile) return null;
    return tile.getAttribute('data-title') || tile.getAttribute('data-size-name');
  }

  function money(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return (cents / 100).toFixed(2);
  }

  function baseTitle(title) {
    return String(title || '').split('|')[0].trim();
  }

  // "original" / "updated" as a shopper-facing label.
  function roleLabel(role) {
    return role === 'original' ? i18n.originalLogo : i18n.updatedLogo;
  }

  // Carries the current query string across the switch. Theme previews
  // (preview_theme_id, _ab, _fd, _sc) and marketing params are on the URL, and dropping
  // them here would kick a reviewer back to the live theme mid-test and lose attribution
  // for everyone else. `variant` is dropped on purpose — it names a variant of the product
  // being left behind — and the size param is re-set from sizeName.
  function productUrl(handle, sizeName) {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      params = null;
    }
    if (!params) {
      return '/products/' + handle +
        (sizeName ? '?' + SIZE_PARAM + '=' + encodeURIComponent(sizeName) : '');
    }

    params.delete('variant');
    params.delete(SIZE_PARAM);
    if (sizeName) params.set(SIZE_PARAM, sizeName);

    var query = params.toString();
    return '/products/' + handle + (query ? '?' + query : '');
  }

  function altUrl(sizeName) {
    return productUrl(state.pair.alternative.handle, sizeName);
  }

  // The info affordance, identical on all three variants: hover on desktop (CSS), tap on
  // mobile (onInfoClick). The tooltip is a SIBLING of the icon, which is what the CSS
  // `:hover ~` rule needs — keep them adjacent wherever this is embedded.
  function infoMarkup() {
    if (!i18n.infoText) return '';
    var id = 'oa-logo-versions-tip-' + (++uid);
    // A real <button>, not a span with role=button: Enter and Space come free, and it is
    // inside #add-to-cart-form, so type="button" is what stops it submitting the form.
    // aria-describedby ties the tooltip to it, so the copy is announced rather than being
    // decoration a screen reader never reaches.
    return '<button type="button" class="oa-logo-versions__info" aria-describedby="' + id + '"' +
      ' aria-label="' + escapeAttr(i18n.infoLabel || i18n.selectLogo) + '">' + INFO_ICON_SVG + '</button>' +
      '<span class="oa-logo-versions__tip" id="' + id + '" role="tooltip">' +
      escapeHtml(i18n.infoText) + '</span>';
  }

  function el(tag, className, html) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (html != null) node.innerHTML = html;
    return node;
  }

  function remove(selector) {
    var nodes = document.querySelectorAll(selector);
    Array.prototype.forEach.call(nodes, function(node) { node.remove(); });
  }

  // --- Rendering ---

  function teardown() {
    // Matches the injected drawer section too — it carries the same base class, and a
    // colour switch makes its product wrong.
    remove('.oa-logo-versions');
    clearMissingHighlight();
    if (state.autoOpenTimer) {
      clearTimeout(state.autoOpenTimer);
      state.autoOpenTimer = null;
    }
  }

  function render() {
    teardown();
    if (!state.pair) return;

    if (state.version === 'a') renderTabs();
    if (state.version === 'b') renderModule();

    // The hint (a) and the module (b) both follow the current selection, and c arms its
    // timer once the data is in.
    refresh();
    if (state.version === 'c') armAutoOpen();
  }

  function renderTabs() {
    // Colour first, then size, then logo — the order agreed with Lara. The picker is
    // inside #oa-switch-sizes, so this goes after that block and before the price section,
    // still within .product-variants. Sibling of the swap region, not inside it, so a
    // colour switch cannot wipe it mid-render.
    var sizes = document.getElementById('oa-switch-sizes');
    var anchor = sizes || document.querySelector('.product-sizes-container');
    if (!anchor || !anchor.parentNode) return;

    var wrap = el('div', 'oa-logo-versions oa-logo-versions__tabs');
    var title = el('div', 'oa-logo-versions__title',
      '<span class="oa-logo-versions__label">' + escapeHtml(i18n.selectLogo) + '</span>' + infoMarkup());

    var list = el('ul', 'oa-logo-versions__tablist');
    // Updated first, always — the rollout is meant to favour the new branding, so it reads
    // as the default rather than as the alternative.
    ['updated', 'original'].forEach(function(role) {
      var isCurrent = state.pair.currentRole === role;
      var item = el('li', 'oa-logo-versions__tab-item');
      // A real anchor for the one that navigates: it announces as a link, takes Enter
      // without a keydown handler, and supports cmd/middle-click into a new tab. The href
      // is kept current by updateTabHrefs, so nothing has to intercept the click.
      var inner = document.createElement(isCurrent ? 'span' : 'a');
      inner.className = 'oa-logo-versions__tab' + (isCurrent ? ' oa-logo-versions__tab--active' : '');
      inner.setAttribute('data-logo-role', role);
      inner.textContent = roleLabel(role);
      if (isCurrent) {
        inner.setAttribute('aria-current', 'page');
      } else {
        inner.setAttribute('href', altUrl(null));
      }
      item.appendChild(inner);
      list.appendChild(item);
    });

    wrap.appendChild(title);
    wrap.appendChild(list);
    anchor.parentNode.insertBefore(wrap, anchor.nextSibling);
  }

  function renderModule() {
    // Anchored off the CTA area, never off the size picker: a sold-out product renders no
    // picker at all, and that is precisely when this module has something to offer.
    var anchor = document.querySelector('.btn--addtowishlist-desktop') ||
                 document.querySelector('.oa-product_price-section');
    if (!anchor || !anchor.parentNode) return;

    var alt = state.pair.alternative;
    var wrap = el('div', 'oa-logo-versions oa-logo-versions__module');
    wrap.setAttribute('hidden', 'hidden');
    // Names the whole block, so the selects and the CTA inside it are not announced adrift
    // of what they belong to.
    var headingId = 'oa-logo-versions-module-heading-' + (++uid);
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-labelledby', headingId);

    var colour = String(alt.title).split('|')[1];
    colour = colour ? colour.trim() : '';

    wrap.innerHTML =
      '<div class="oa-logo-versions__module-head">' +
        '<div class="oa-logo-versions__module-heading">' +
          '<span class="oa-logo-versions__label" id="' + headingId + '">' +
          escapeHtml(i18n.alternativeHeading) + '</span>' +
          infoMarkup() + '</div>' +
        '<p class="oa-logo-versions__module-sub">' + escapeHtml(i18n.alternativeSubline) + '</p>' +
      '</div>' +
      '<div class="oa-logo-versions__module-body">' +
        // Hidden from AT and skipped by the keyboard: it is the same destination as the
        // title link directly beside it, and two links to one product read as a stutter.
        '<a class="oa-logo-versions__module-thumb" href="' + escapeAttr(productUrl(alt.handle, null)) +
          '" tabindex="-1" aria-hidden="true">' +
          (alt.image ? '<img src="' + escapeAttr(alt.image) + '" alt="" loading="lazy">' : '') +
        '</a>' +
        '<div class="oa-logo-versions__module-info">' +
          '<a class="oa-logo-versions__module-title" href="' + escapeAttr(productUrl(alt.handle, null)) + '">' +
            escapeHtml(baseTitle(alt.title)) + '</a>' +
          '<div class="oa-logo-versions__module-price">' + money(alt.price) + '</div>' +
          '<select class="oa-logo-versions__module-select oa-logo-versions__module-colour" aria-label="' +
            escapeAttr(i18n.colourLabel) + '">' + colourOptions() + '</select>' +
          '<select class="oa-logo-versions__module-select oa-logo-versions__module-size" aria-label="' +
            escapeAttr(i18n.sizeLabel) + '"></select>' +
          '<button type="button" class="oa-logo-versions__module-cta btn btn-cta btn-cta--rounded">' +
            escapeHtml(i18n.quickAdd) + '</button>' +
        '</div>' +
      '</div>';

    anchor.parentNode.insertBefore(wrap, anchor.nextSibling);
    fillModuleSizes(null);
  }

  // The colour currently shown in the module. Starts as the direct alternative — the same
  // colour the shopper is looking at — and moves when they pick another.
  function moduleProduct() {
    return state.moduleColour || (state.pair && state.pair.alternative);
  }

  // Every colour of the alternative branding version, the matching one selected. The list
  // comes from the search hits, so it costs no extra request; picking a colour is what
  // pulls that product's variants.
  // Deduped, sorted, and guaranteed to contain the direct alternative — shared by the
  // module's <select> and the drawer block's swatches so the two can never disagree.
  function colourEntries() {
    if (!state.pair) return [];
    var alt = state.pair.alternative;
    var seen = {};
    var opts = [];

    (state.pair.colours || []).forEach(function(entry) {
      if (!entry.colour || seen[entry.id]) return;
      seen[entry.id] = true;
      opts.push(entry);
    });
    if (!seen[alt.id] && !opts.some(function(o) { return o.handle === alt.handle; })) {
      opts.unshift({ id: alt.id, handle: alt.handle, title: alt.title, colour: colourOf(alt.title) });
    }

    opts.sort(function(a, b) { return a.colour.localeCompare(b.colour); });
    return opts;
  }

  function colourOptions() {
    if (!state.pair) return '';
    var current = moduleProduct();
    return colourEntries().map(function(entry) {
      var selected = entry.handle === (current && current.handle) ? ' selected' : '';
      return '<option value="' + escapeAttr(String(entry.id)) + '" data-handle="' +
        escapeAttr(entry.handle) + '"' + selected + '>' + escapeHtml(titleCase(entry.colour)) + '</option>';
    }).join('');
  }

  function colourOf(title) {
    var part = String(title || '').split('|')[1];
    return part ? part.trim() : '';
  }

  function titleCase(value) {
    return String(value || '').replace(/\b\w/g, function(c) { return c.toUpperCase(); });
  }

  // Swap the module over to another colour of the same branding version. The size list,
  // price, image and links all belong to the product, so they move together.
  // The size showing in the module's own picker, which is not necessarily the sold-out one
  // that opened it — the shopper may have changed it since.
  function selectedModuleSizeName() {
    var select = document.querySelector('.oa-logo-versions__module-size');
    if (!select || select.selectedIndex < 0) return null;
    var option = select.options[select.selectedIndex];
    return option ? option.textContent.trim() : null;
  }

  function selectModuleColour(gid) {
    if (!window.OALogoVersionsApi) return;
    var module = document.querySelector('.oa-logo-versions__module');
    if (module) module.classList.add('oa-logo-versions__module--loading');

    // Captured before the swap: carry the shopper's own choice across the colour change
    // rather than snapping back to the size that was sold out on the PDP.
    var keepSize = selectedModuleSizeName() || state.lastMissingSize;

    window.OALogoVersionsApi.fetchColour(gid).then(function(product) {
      if (module) module.classList.remove('oa-logo-versions__module--loading');
      if (!product) return;
      state.moduleColour = product;
      repaintModule(keepSize);
    });
  }

  // Everything in the module that names the product. The colour select is left alone: it
  // is what the shopper just used, and rewriting it would drop focus mid-interaction.
  function repaintModule(preferredSizeName) {
    var product = moduleProduct();
    if (!product) return;
    var url = productUrl(product.handle);

    var thumb = document.querySelector('.oa-logo-versions__module-thumb');
    if (thumb) {
      thumb.setAttribute('href', url);
      var img = thumb.querySelector('img');
      if (img && product.image) img.setAttribute('src', product.image);
    }
    var title = document.querySelector('.oa-logo-versions__module-title');
    if (title) {
      title.setAttribute('href', url);
      title.textContent = baseTitle(product.title);
    }
    var price = document.querySelector('.oa-logo-versions__module-price');
    if (price) price.innerHTML = money(product.price);

    fillModuleSizes(preferredSizeName || state.lastMissingSize);
  }

  // Only in-stock sizes are listed, so a preferred size the new colour does not have simply
  // never matches — and the browser lands on the first option, which is the first available
  // size. That is the fallback; it needs no branch of its own.
  function fillModuleSizes(preferredSizeName) {
    var select = document.querySelector('.oa-logo-versions__module-size');
    if (!select) return;
    select.innerHTML = '';

    var product = moduleProduct();
    ((product && product.variants) || []).forEach(function(variant) {
      if (!variant.available) return;
      var label = variant.option1 || variant.title;
      var option = document.createElement('option');
      option.value = variant.id;
      option.textContent = label;
      // sameSize, not sizeKey: on a length product sizeKey alone calls "XS - Short" and
      // "XS - Long" the same size and would carry the wrong length across.
      if (preferredSizeName && sameSize(label, preferredSizeName)) option.selected = true;
      select.appendChild(option);
    });
  }

  // --- Reacting to the selection ---

  function refresh() {
    if (!state.pair) return;
    var tile = selectedTile();
    var sizeName = tileSizeName(tile);
    var soldOutHere = !!tile && tile.classList.contains('disabled');

    // A selected size only ever reads as sold out here through lastMissingSize — the tile
    // handlers never mark a disabled tile active, so a click on one leaves the previous
    // selection in place.
    var missing = state.lastMissingSize || (soldOutHere ? sizeName : null);
    var offerable = !!missing && availableOnAlternative(missing);

    // Nothing buyable here at all: there is no size to have asked about, so offer the
    // alternative on the strength of the product being sold out rather than the selection.
    if (!offerable && fullySoldOutHere()) {
      var fallback = firstAvailableAlternativeSize();
      if (fallback) {
        missing = fallback;
        offerable = true;
      }
    }

    // Both surfaces are about the picker as a whole, not the current selection: with
    // fragmented stock the shopper has to be told the other version exists BEFORE they pick
    // the size that is missing. Gating on a selection meant a part-stocked product showed
    // nothing at all until a sold-out size was clicked, which is most of the catalogue.
    //
    // Where the shopper HAS asked about a size, that one wins — the module should offer what
    // they went looking for, not the first gap in the size run.
    //
    // The two differ on one point: the message only invites the shopper to go and look, so
    // it shows for any sold-out size. The module has to name a size and put a real variant
    // in the cart, so it still needs one the sibling actually has.
    if (state.version === 'a') {
      toggleHint(hasSoldOutSizeHere(), i18n.switchHint);
      updateTabHrefs();
    }
    // Same placement as version a, different copy: here it points down at the module rather
    // than across at a switcher. Tied to the module actually being offerable, so it never
    // says "check below" with nothing below.
    if (state.version === 'b') {
      toggleHint(!!firstOfferableMissingSize(), i18n.moduleHint, 'oa-logo-versions__hint--plain');
    }
    if (state.version === 'b') toggleModule(offerable ? missing : firstOfferableMissingSize());
  }

  // Whether this product has any size a shopper cannot buy right now. Deliberately does not
  // ask whether the OTHER version has it: the copy says "switch to X to SEE IF it is still
  // in stock", so it is an invitation to check, not a promise. Gating it on the sibling's
  // stock hid the message on exactly the products where the shopper most needs to know a
  // second version exists.
  function hasSoldOutSizeHere() {
    if (fullySoldOutHere()) return true;
    return visibleTiles().some(function(tile) {
      return tile.classList.contains('disabled');
    });
  }

  // Carries the chosen size on the link itself, so the browser navigates natively and a
  // cmd-click opens the right url. Nothing intercepts the click any more.
  function updateTabHrefs() {
    if (!state.pair) return;
    var sizeName = state.lastMissingSize || tileSizeName(selectedTile());
    var link = document.querySelector('a.oa-logo-versions__tab');
    if (link) link.setAttribute('href', altUrl(sizeName));
  }

  function toggleHint(show, copy, modifier) {
    remove('.oa-logo-versions__hint');
    if (!show || !copy) return;

    // {logo} is substituted with the version being switched TO, so directional wording
    // reads correctly from either side ("switch to Original Logo" on the updated PDP,
    // "switch to Updated Logo" on the original). The shipped copy is generic and carries no
    // placeholder, which makes this a no-op — putting {logo} back in the Liquid string is
    // all it takes to go directional again.
    var hint = el('div', 'oa-logo-versions oa-logo-versions__hint' + (modifier ? ' ' + modifier : ''),
      escapeHtml(copy.replace('{logo}', roleLabel(state.pair.alternativeRole))));

    // Directly under the size tiles and above the size assistant, which lives further down
    // in the same container. On a sold-out product there is no picker at all, so it falls
    // back to sitting under the tabs rather than not showing.
    var tiles = document.querySelector('.product-sizes-container ul.product-sizes-wrapper') ||
                document.querySelector('.product-sizes-container ul.product-sizes');
    if (tiles && tiles.parentNode) {
      tiles.parentNode.insertBefore(hint, tiles.nextSibling);
      return;
    }
    var container = document.querySelector('.product-sizes-container');
    if (container) {
      container.appendChild(hint);
      return;
    }
    var tabs = document.querySelector('.oa-logo-versions__tabs');
    if (tabs && tabs.parentNode) tabs.parentNode.insertBefore(hint, tabs.nextSibling);
  }

  function toggleModule(sizeName) {
    var module = document.querySelector('.oa-logo-versions__module');
    if (!module) return;
    if (!sizeName) {
      module.setAttribute('hidden', 'hidden');
      return;
    }
    fillModuleSizes(sizeName);
    module.removeAttribute('hidden');
  }

  function clearMissingHighlight() {
    var marked = document.querySelectorAll('.oa-logo-versions__size--missing');
    Array.prototype.forEach.call(marked, function(node) {
      node.classList.remove('oa-logo-versions__size--missing');
    });
  }

  // Put the shopper back where they were: the sold-out size they asked about, highlighted
  // on the PDP they are still standing on.
  function highlightMissing(sizeName) {
    clearMissingHighlight();
    if (!sizeName) return;
    visibleTiles().forEach(function(tile) {
      if (sameSize(tileSizeName(tile), sizeName)) {
        tile.classList.add('oa-logo-versions__size--missing');
      }
    });
  }

  // --- Version c: the quick-add sidebar ---

  function anyDrawerOpen() {
    if (document.querySelector('.oa-body.modal-open')) return true;
    if (document.querySelector('#sale-pop-up.in')) return true;
    if (window.sidecart && window.sidecart._data && window.sidecart._data.show) return true;
    if (window.sidebarBackInStock && window.sidebarBackInStock._data && window.sidebarBackInStock._data.show) return true;
    if (window.sidebar && window.sidebar._data && window.sidebar._data.show) return true;
    return false;
  }

  function openAlternativeSidebar(sizeName, triggerEl) {
    var sidebar = window.sidebar;
    if (!sidebar || typeof sidebar.onShow !== 'function') return false;

    var variant = altVariantForSize(sizeName);
    if (!variant || !variant.available) return false;

    state.lastMissingSize = sizeName;
    sidebar.altNote = {
      heading: i18n.alternativeHeading,
      text: i18n.alternativeSubline,
      // Same icon and copy as the other two variants. Passed rather than duplicated into
      // the Liquid template so the SVG has one definition.
      info: i18n.infoText,
      infoLabel: i18n.infoLabel || i18n.alternativeHeading,
      icon: INFO_ICON_SVG
    };
    sidebar.onShow(
      state.pair.alternative.handle,
      variant.id,
      false,
      null,
      null,
      '1',
      undefined,
      triggerEl || document.body
    );
    return true;
  }

  function markSeen() {
    if (!AUTO_OPEN_ONCE_PER_SESSION || isForcedVersion()) return;
    try { sessionStorage.setItem(SEEN_KEY_PREFIX + state.handle, '1'); } catch (e) {}
  }

  function alreadySeen() {
    if (!AUTO_OPEN_ONCE_PER_SESSION) return false;
    // A version pinned through the URL is someone reviewing the test, not a shopper, and
    // they need the popup on every reload. The cap is a shopper-facing courtesy, so it is
    // dropped for the QA entry points rather than making them clear storage by hand.
    if (isForcedVersion()) return false;
    try { return sessionStorage.getItem(SEEN_KEY_PREFIX + state.handle) === '1'; } catch (e) {}
    return false;
  }

  // Whether this page load pinned the version explicitly, by hash or query param — the
  // same two QA entry points header.js reads in its own readSelection().
  function isForcedVersion() {
    var hash = (window.location.hash || '').toLowerCase();
    if (hash.indexOf('#logo-versions-') === 0) return true;
    try {
      return !!new URLSearchParams(window.location.search).get('logo_versions_ab');
    } catch (e) {}
    return false;
  }

  // The first visible size that is sold out here and buyable on the sibling — or, when
  // this product is gone entirely and renders no picker, whatever the sibling still has.
  function firstOfferableMissingSize() {
    var tiles = visibleTiles();
    for (var i = 0; i < tiles.length; i++) {
      if (!tiles[i].classList.contains('disabled')) continue;
      var name = tileSizeName(tiles[i]);
      if (availableOnAlternative(name)) return name;
    }
    if (fullySoldOutHere()) return firstAvailableAlternativeSize();
    return null;
  }

  function armAutoOpen() {
    if (state.autoOpenTimer) clearTimeout(state.autoOpenTimer);
    if (alreadySeen()) return;
    state.autoOpenTimer = setTimeout(maybeAutoOpen, AUTO_OPEN_DELAY_MS);
  }

  function maybeAutoOpen() {
    state.autoOpenTimer = null;
    if (state.version !== 'c' || !state.pair || alreadySeen()) return;
    // Something is already on screen — a cart drawer, the notify sidebar, the sale popup.
    // Stacking a second drawer on top of it would leave both fighting over modal-open on
    // .oa-body, and the shopper is mid-task in the one they opened themselves.
    if (anyDrawerOpen()) return;

    var sizeName = firstOfferableMissingSize();
    if (!sizeName) return;
    if (openAlternativeSidebar(sizeName, null)) markSeen();
  }

  // --- Version b: the alternative, stacked above the back-in-stock form ---

  // Rather than rebuild the notify form inside the quick-add drawer — it is a second Vue
  // app with its own emarsys submit, phone validation and consent copy — the alternative
  // is injected into the drawer that already carries that form and already opens on this
  // click. The border under it is what separates the two halves in the design.
  function renderBisAlternative(sizeName) {
    removeBisAlternative();
    if (state.version !== 'b' || !state.pair) return;

    var body = document.querySelector('#sidebar-back-in-stock .sidebar-bis--body');
    var form = body && body.querySelector('#notify-me-wrapper');
    if (!body || !form) return;

    // Everything below is the colour on screen, which is not the direct sibling once the
    // shopper has picked another swatch — its own sizes, price, image and links.
    var shown = moduleProduct();
    var variant = variantForSize(shown, sizeName) ||
      variantForSize(shown, firstAvailableSizeOf(shown));
    if (!variant) return;

    // Whichever colour of the alternative is on screen, not necessarily the direct sibling.
    var alt = moduleProduct();
    var colour = colourOf(alt.title);

    var section = el('div', 'oa-logo-versions oa-logo-versions__bis');
    section.innerHTML =
      '<div class="oa-logo-versions__bis-head">' +
        '<div class="oa-logo-versions__bis-heading">' +
          '<span class="oa-logo-versions__label">' + escapeHtml(i18n.alternativeHeading) + '</span>' +
          infoMarkup() + '</div>' +
        '<p class="oa-logo-versions__bis-sub">' + escapeHtml(i18n.alternativeSubline) + '</p>' +
      '</div>' +
      '<div class="oa-logo-versions__bis-body">' +
        '<a class="oa-logo-versions__bis-thumb" href="' + escapeAttr(productUrl(alt.handle, null)) +
          '" tabindex="-1" aria-hidden="true">' +
          (alt.image ? '<img src="' + escapeAttr(alt.image) + '" alt="" loading="lazy">' : '') +
        '</a>' +
        // A direct child of the body, not of the info column: that is what lets the grid
        // put it under the image on mobile and top-right of the info on desktop, from one
        // piece of markup.
        '<a class="oa-logo-versions__bis-details" href="' + escapeAttr(productUrl(alt.handle, null)) + '">' +
          escapeHtml(i18n.seeFullDetails) + '</a>' +
        '<div class="oa-logo-versions__bis-info">' +
          '<div class="oa-logo-versions__bis-title">' + escapeHtml(baseTitle(alt.title)) + '</div>' +
          (colour ? '<div class="oa-logo-versions__bis-colour"><span>' + escapeHtml(i18n.colourLabel) +
            ':</span> ' + escapeHtml(titleCase(colour)) + '</div>' : '') +
          bisColourDots(alt) +
          '<div class="oa-logo-versions__bis-sizelabel">' + escapeHtml(i18n.selectSizeLabel) + '</div>' +
          '<ul class="oa-logo-versions__bis-sizes">' + bisSizeTiles(variant) + '</ul>' +
          '<div class="oa-logo-versions__bis-price">' + money(variant.price) + '</div>' +
          '<button type="button" class="oa-logo-versions__bis-cta btn btn-cta btn-cta--rounded">' +
            escapeHtml(i18n.addToCart) + '</button>' +
        '</div>' +
      '</div>';

    body.insertBefore(section, form);
  }

  // Swatches rather than a dropdown, matching the colour rail in the quick-add drawer. The
  // classes are the theme's own: `[data-bgcolor='<Colour Name>']` is a global map in
  // oner_mk.scss, so the dot gets its colour with no work here — and stays right when
  // merchandising adds a shade.
  function bisColourDots(current) {
    if (!state.pair) return '';
    var entries = colourEntries();
    if (entries.length < 2) return '';

    var dots = entries.map(function(entry) {
      var isCurrent = entry.handle === current.handle;
      return '<div class="sidebar--color-item-box oa-logo-versions__bis-colour-box">' +
        '<button type="button" class="sidebar--color-item oa-logo-versions__bis-colour-item' +
          (isCurrent ? ' active' : '') + '" data-colour-id="' + escapeAttr(String(entry.id)) + '"' +
          (isCurrent ? ' aria-current="true"' : '') +
          ' aria-label="' + escapeAttr(titleCase(entry.colour)) + '">' +
          '<span class="sidebar--color-item-bg" data-handle="' + escapeAttr(entry.handle) +
          '" data-bgcolor="' + escapeAttr(titleCase(entry.colour)) + '"></span>' +
        '</button></div>';
    }).join('');

    return '<div class="oa-logo-versions__bis-colours oa-fashion-color-switch">' + dots + '</div>';
  }

  function bisSizeTiles(selected) {
    return ((moduleProduct() || {}).variants || []).map(function(variant) {
      var label = variant.option1 || variant.title;
      var classes = 'oa-logo-versions__bis-size';
      if (!variant.available) classes += ' oa-logo-versions__bis-size--disabled';
      if (variant.id === selected.id) classes += ' oa-logo-versions__bis-size--active';
      // Same diagonal as the PDP tiles. .oa-cross-out-svg is styled globally in
      // oner_mk.scss, so reusing the class gives the identical stroke rather than a
      // second definition that can drift from it.
      var strike = variant.available ? '' :
        '<svg class="oa-cross-out-svg"><line class="line" x1="0" y1="100%" x2="100%" y2="0"></line></svg>';
      return '<li class="' + classes + '" data-variant-id="' + variant.id + '">' +
        strike + '<span>' + escapeHtml(label) + '</span></li>';
    }).join('');
  }

  function removeBisAlternative() {
    remove('.oa-logo-versions__bis');
  }

  function selectedBisVariantId() {
    var active = document.querySelector('.oa-logo-versions__bis-size--active');
    return active ? parseInt(active.getAttribute('data-variant-id'), 10) : null;
  }

  function onBisClick(event) {
    if (state.version !== 'b' || !state.pair || !event.target.closest) return;

    var dot = event.target.closest('.oa-logo-versions__bis-colour-item');
    if (dot) {
      event.preventDefault();
      if (dot.classList.contains('active')) return;
      var gid = dot.getAttribute('data-colour-id');
      if (!gid || !window.OALogoVersionsApi) return;
      window.OALogoVersionsApi.fetchColour(gid).then(function(product) {
        if (!product) return;
        state.moduleColour = product;
        // Cheaper to rebuild than to repaint: the block is small and every part of it —
        // image, title, colour, sizes, price, links — belongs to the product that changed.
        renderBisAlternative(state.lastMissingSize);
      });
      return;
    }

    var size = event.target.closest('.oa-logo-versions__bis-size');
    if (size) {
      if (size.classList.contains('oa-logo-versions__bis-size--disabled')) return;
      var tiles = document.querySelectorAll('.oa-logo-versions__bis-size');
      Array.prototype.forEach.call(tiles, function(node) {
        node.classList.remove('oa-logo-versions__bis-size--active');
      });
      size.classList.add('oa-logo-versions__bis-size--active');
      var priceEl = document.querySelector('.oa-logo-versions__bis-price');
      // The tiles were rendered from the colour on screen, so the price must come from the
      // same product — reading the direct sibling here showed another colour's price.
      var picked = ((moduleProduct() || {}).variants || []).filter(function(v) {
        return v.id === parseInt(size.getAttribute('data-variant-id'), 10);
      })[0];
      if (priceEl && picked) priceEl.innerHTML = money(picked.price);
      return;
    }

    var cta = event.target.closest('.oa-logo-versions__bis-cta');
    if (!cta) return;
    event.preventDefault();
    var variantId = selectedBisVariantId();
    if (!variantId || typeof window.onQuickAddClicked !== 'function') return;
    // The colour on screen, so tracking and the success event name what actually went in.
    var product = moduleProduct();
    if (!product) return;

    // onQuickAddClicked opens the cart drawer once the POST resolves. Close this one first
    // or the two stack, and both write `modal-open` on .oa-body — whichever closes last
    // strips the scroll lock the other still needs.
    var bis = window.sidebarBackInStock;
    if (bis && bis._data) bis._data.show = false;

    window.onQuickAddClicked(cta, variantId, product.handle, product.title, product.image);
  }


  // --- Event handling ---

  // Capture phase, on document: the tiles are replaced wholesale by every colour switch,
  // so nothing may be bound per element, and running before the target-phase jQuery
  // handlers is what lets us take the click off the back-in-stock drawer.
  function onSizeClickCapture(event) {
    if (!state.pair || !state.version) return;
    var tile = event.target.closest && event.target.closest(SIZE_TILE);
    if (!tile) return;
    if (tile.closest('#cts-modal') || tile.closest('#product-stl')) return;

    if (!tile.classList.contains('disabled')) {
      // An in-stock size clears the sold-out question entirely.
      state.lastMissingSize = null;
      clearMissingHighlight();
      // The tile's own handler has not run yet, so the active class moves after this —
      // refresh on the next tick to read the settled selection.
      setTimeout(refresh, 0);
      return;
    }

    var sizeName = tileSizeName(tile);
    if (!availableOnAlternative(sizeName)) {
      // Sold out on both versions. Leave the click alone: the back-in-stock drawer is the
      // right answer and is already wired to it.
      return;
    }

    state.lastMissingSize = sizeName;

    // Who may take the click off the back-in-stock drawer:
    //   c  always — it opens its own popup in the drawer's place.
    //   b  never — it needs the drawer, and injects the alternative into it.
    //   a  only when the flag asks. Its mechanism is the tab, not the click, so by default
    //      the drawer opens exactly as it does today. Suppressing it here without putting
    //      something else on screen made a sold-out size a dead click: the drawer never
    //      opened and the only visible change was a border on the tile.
    var suppress = state.version === 'c' ||
      (state.version === 'a' && window.oaLogoVersionsSuppressBackInStock === true);
    if (suppress) {
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    }

    if (state.version === 'c') {
      if (openAlternativeSidebar(sizeName, tile)) markSeen();
      return;
    }

    highlightMissing(sizeName);
    refresh();

    if (state.version === 'b') {
      // The drawer is opened by the handler this click is still travelling to, so the
      // section is injected once that has run and the body exists.
      setTimeout(function() { renderBisAlternative(sizeName); }, 0);
    }

    // Nothing else is going to happen on screen, so point at what the shopper should do
    // next. Without this the click reads as broken.
    if (suppress) flagSwitcher();
  }

  // Bring the switcher into view and pulse it, once. The class is removed on animation end
  // so a second sold-out click plays it again.
  function flagSwitcher() {
    var tabs = document.querySelector('.oa-logo-versions__tabs');
    if (!tabs) return;

    var rect = tabs.getBoundingClientRect();
    var offscreen = rect.top < 0 || rect.bottom > (window.innerHeight || 0);
    if (offscreen && tabs.scrollIntoView) {
      tabs.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    tabs.classList.remove('oa-logo-versions__tabs--flag');
    // Forces the class removal to land before it goes back on, so the animation restarts
    // rather than being treated as unchanged.
    void tabs.offsetWidth;
    tabs.classList.add('oa-logo-versions__tabs--flag');
  }

  // The info tooltip, on its own handler and NOT gated on the version: the icon appears in
  // all three, and living inside onTabClick meant its `version !== 'a'` guard silently
  // swallowed every tap in b and c. Desktop opens it on hover in CSS; this is the tap path.
  function onInfoClick(event) {
    if (!event.target.closest) return;
    var info = event.target.closest('.oa-logo-versions__info');

    // Scoped to the icon that was tapped. There can be several on a page — the b module and
    // the block injected into the notify drawer both carry one — and a document-wide
    // querySelector would open whichever happened to be first in the DOM.
    if (info) {
      var tip = info.parentNode && info.parentNode.querySelector('.oa-logo-versions__tip');
      if (!tip) return;
      event.preventDefault();
      var wasOpen = tip.classList.contains('oa-logo-versions__tip--open');
      closeAllTips();
      if (!wasOpen) tip.classList.add('oa-logo-versions__tip--open');
      return;
    }

    // A tap anywhere else dismisses it — a tooltip this small has no close button.
    closeAllTips();
  }

  function closeAllTips() {
    var open = document.querySelectorAll('.oa-logo-versions__tip--open');
    Array.prototype.forEach.call(open, function(tip) {
      tip.classList.remove('oa-logo-versions__tip--open');
    });
  }

  function onModuleChange(event) {
    if (state.version !== 'b' || !state.pair) return;
    var select = event.target.closest && event.target.closest('.oa-logo-versions__module-colour');
    if (!select || !select.value) return;
    selectModuleColour(select.value);
  }

  function onModuleClick(event) {
    if (state.version !== 'b' || !state.pair) return;
    var cta = event.target.closest && event.target.closest('.oa-logo-versions__module-cta');
    if (!cta) return;
    event.preventDefault();

    var select = document.querySelector('.oa-logo-versions__module-size');
    var variantId = select && select.value ? parseInt(select.value, 10) : null;
    if (!variantId || typeof window.onQuickAddClicked !== 'function') return;

    var product = moduleProduct();
    if (!product) return;
    window.onQuickAddClicked(cta, variantId, product.handle, product.title, product.image);
  }

  // Enter and Space on the tabs and the info trigger, which are list items rather than
  // buttons so they can inherit the picker styling.
  // Escape closes the tooltip. Enter/Space on the trigger and the tab links are native
  // now that both are real elements, so nothing else needs a key handler.
  function onKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') closeAllTips();
  }

  // A size carried over from the other version's PDP. Select it if it is buyable here;
  // if it sold out here too, say so rather than silently landing on nothing.
  function applyIncomingSize() {
    var incoming;
    try {
      incoming = new URLSearchParams(window.location.search).get(SIZE_PARAM);
    } catch (e) {}
    if (!incoming) return;

    // Drop the parameter either way, so a refresh or a shared link is a clean PDP.
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete(SIZE_PARAM);
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    } catch (e) {}

    // A length product hides the tiles of every length but the active one, so arriving with
    // "XS - Long" while Regular is showing would find nothing. Move the length picker first
    // and let its handler reveal the right tiles before looking for the size.
    var wantedLength = lengthKey(incoming);
    if (wantedLength) {
      var lengthTile = document.querySelector(
        '.fashion-info-sticky .product-length-wrapper .oa-product-sizes--item[data-selection-size="' +
        wantedLength + '"]');
      if (lengthTile && !lengthTile.classList.contains('active')) {
        clickTile(lengthTile);
        setTimeout(function() { selectIncomingSize(incoming); }, 0);
        return;
      }
    }
    selectIncomingSize(incoming);
  }

  function selectIncomingSize(incoming) {
    var match = null;
    visibleTiles().forEach(function(tile) {
      if (!match && sameSize(tileSizeName(tile), incoming)) match = tile;
    });
    if (!match) return;

    if (match.classList.contains('disabled')) {
      state.lastMissingSize = tileSizeName(match);
      highlightMissing(state.lastMissingSize);
      refresh();
    } else {
      clickTile(match);
    }
  }

  // product-single.layout.js binds these through jQuery, so a jQuery trigger is what the
  // handler is actually listening for; the native click is the fallback.
  function clickTile(tile) {
    if (window.jQuery) {
      window.jQuery(tile).trigger('click');
    } else {
      tile.click();
    }
  }

  // --- Boot ---

  function resolve(handle, title) {
    if (!window.OALogoVersionsApi) return;
    window.OALogoVersionsApi.findAlternative(handle, title).then(function(pair) {
      // A colour switch may have moved on while this was in flight.
      if (handle !== state.handle) return;
      state.pair = pair;
      if (!pair) return;
      render();
      applyIncomingSize();
    });
  }

  function start() {
    var version = activeVersion();
    state.version = version;

    if (!version) {
      teardown();
      state.pair = null;
      return;
    }

    var cfg = config();
    if (!cfg) return;

    // Handle first: the map is keyed by it, and it decides entry alongside the tag.
    state.handle = handleFromUrl() || cfg.handle;

    if (!cfg.hasTag && !isMapped(state.handle)) return;
    if (isComingSoon() || isExcludedProduct(cfg.tags)) return;

    if (state.pair) {
      // Version changed mid-session and the pair is already resolved.
      render();
      return;
    }
    resolve(state.handle, cfg.title);
  }

  function onColorSwitch(event) {
    if (!state.version) return;
    var detail = event.detail || {};
    var product = detail.product;
    var handle = detail.handle;
    if (!handle || !product) return;

    // The swapped-in colour is a different product, with its own sibling and its own tags.
    teardown();
    state.pair = null;
    state.lastMissingSize = null;
    state.moduleColour = null;
    state.handle = handle;

    var tag = window.oaLogoVersionsTag;
    var tags = Array.isArray(product.tags) ? product.tags : [];
    var tagged = !!tag && tags.some(function(t) {
      return String(t).toLowerCase() === String(tag).toLowerCase();
    });
    if (!tagged && !isMapped(handle)) return;
    if (isExcludedProduct(tags)) return;

    resolve(handle, product.title);
  }

  function init() {
    if (state.booted) return;
    state.booted = true;

    var cfg = config();
    i18n = (cfg && cfg.i18n) || {};

    // Bound once, unconditionally: they all return immediately while no version is active,
    // and the A/B tool can assign one at any point after this.
    document.addEventListener('click', onSizeClickCapture, true);
    document.addEventListener('click', onInfoClick);
    document.addEventListener('click', onModuleClick);
    document.addEventListener('change', onModuleChange);
    document.addEventListener('click', onBisClick);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('oa:colorswitch', onColorSwitch);
    document.addEventListener('oa:variantchange', function() { setTimeout(refresh, 0); });
    document.addEventListener('oa:logoversionsversion', start);

    // The sidebar closed on an offer we made. Leave the shopper on this PDP with the
    // sold-out size they asked about marked, rather than on an unchanged page.
    document.addEventListener('oa:quickaddsidebarclose', function() {
      if (state.version === 'c') highlightMissing(state.lastMissingSize);
    });

    // The sale popup runs on its own 15s timer and would stack on top of ours. Close ours
    // rather than fight it — the shopper can still reach the alternative from the PDP.
    if (window.jQuery) {
      window.jQuery(document).on('show.bs.modal', '#sale-pop-up', function() {
        if (window.sidebar && window.sidebar._data && window.sidebar._data.show && window.sidebar.altNote) {
          window.sidebar.onClose();
        }
      });
    }

    start();
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/"/g, '&quot;');
  }

  window.oaLogoVersions = {
    start: start,
    refresh: refresh,
    getPair: function() { return state.pair; },
    getVersion: function() { return state.version; },

    // QA: clear the once-per-session cap and re-arm the timer, for testing version c
    // repeatedly without reaching into sessionStorage. Not needed when the version came
    // from the hash or query param — those are already exempt from the cap.
    reset: function() {
      try { sessionStorage.removeItem(SEEN_KEY_PREFIX + state.handle); } catch (e) {}
      state.lastMissingSize = null;
      if (state.version === 'c') armAutoOpen();
      return 'armed: ' + AUTO_OPEN_DELAY_MS + 'ms';
    },

    // QA: open the popup now rather than waiting out the delay.
    open: function() {
      if (!state.pair) return 'no alternative resolved';
      var sizeName = firstOfferableMissingSize();
      if (!sizeName) return 'nothing offerable on the alternative';
      return openAlternativeSidebar(sizeName, null) ? 'opened: ' + sizeName : 'sidebar unavailable';
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
